import { defineStore } from "pinia";
import { reactive, ref, set, del } from "vue";
import { useIQChatStore } from "./iqChatStore";
import { useNotificationStore } from "@/store/pinia/notifications";
import { suggestQuizStream } from "@/api/suggestQuizApi";
import { suggestInspectionProtocolStream } from "@/api/suggestInspectionProtocolApi";
import {
  MAX_CHECKPOINT_QUIZ_QUESTIONS,
  mapSuggestedQuizQuestionsToApi,
} from "@/utils/buildSuggestQuizPayload";
import {
  MAX_INSPECTION_STEPS,
  filterDuplicateSuggestedInspectionSteps,
  mapSuggestedStepsToEditorRows,
} from "@/utils/buildSuggestInspectionProtocolPayload";
import {
  changeCheckpointV2,
  addCheckpointQuestion,
  addCheckpointInspectionStep,
  deleteCheckpointInspectionStep,
  replaceCheckpointQuestions,
} from "@/api/responsibilityAPI";

const QUIZ_SUGGEST_STALL_TIMEOUT_MS = 30000;
const INSPECTION_STREAM_TIMEOUT_MS = 360000;

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

function normalizeCompetenceCategory(category) {
  const normalized = (category || "ability").toLowerCase();
  return normalized === "behavior" ? "behaviour" : normalized;
}

function mapInstructionStep(step = {}) {
  const seqRaw = step.step_sequence ?? step.step_no;
  const step_sequence =
    seqRaw != null && seqRaw !== "" && Number.isFinite(Number(seqRaw)) ? Number(seqRaw) : null;
  return {
    competence_category: normalizeCompetenceCategory(
      step.competence_category ?? step.competency_category
    ),
    inspector_action: step.inspector_action ?? step.action ?? step.chapter_title ?? "",
    look_for: step.look_for ?? step.chapter_description ?? "",
    pass_criteria: step.pass_criteria ?? step.chapter_summary ?? "",
    fail_criteria: step.fail_criteria ?? "",
    timestamp_ref:
      step.timestamp_ref ??
      (step.start_time && step.end_time ? `${step.start_time} - ${step.end_time}` : ""),
    video_jump_url: step.video_jump_url ?? "",
    step_sequence,
  };
}

function buildAddInspectionStepApiPayload(step) {
  const raw = String(step?.competence_category ?? "ability").toLowerCase();
  let competence_category = "Ability";
  if (raw === "knowledge") competence_category = "Knowledge";
  else if (raw === "behaviour" || raw === "behavior") competence_category = "Behavior";
  return {
    step: {
      competence_category,
      inspector_action: step?.inspector_action != null ? String(step.inspector_action).trim() : "",
      look_for: step?.look_for != null ? String(step.look_for).trim() : "",
      pass_criteria: step?.pass_criteria != null ? String(step.pass_criteria).trim() : "",
      fail_criteria: step?.fail_criteria != null ? String(step.fail_criteria).trim() : "",
    },
  };
}

function makeRun(kind, isVideo) {
  return {
    kind,
    isVideo: !!isVideo,
    /** 'thinking' | 'done' | 'failed' | null */
    status: "thinking",
    /** null | 'thinking' | 'failed' (article/root banner) */
    bannerState: isVideo ? null : "thinking",
    /** null | 'connecting' | 'streaming' | 'stalled' (video panel status) */
    videoStatus: isVideo ? "connecting" : null,
    error: null,
    startedAt: Date.now(),
    finishedAt: null,
    /** Live editor should re-fetch the checkpoint after a background persist. */
    refreshPending: false,
    /** Inspection on an active checkpoint: switch the edit bar to Proposed Changes. */
    switchToProposedTab: false,
    /** ER-12788 — quiz questions this run replaces; becomes the undo snapshot on success. */
    undoSnapshot: null,
  };
}

/**
 * ER-12337 — durable, checkpoint-keyed runners for "Suggest quiz" and "Suggest inspection
 * protocol". Both long-running IQ SSE streams used to live inside CheckpointEditFormV2 and
 * were aborted on unmount, so a tab switch dropped the in-flight suggestion. They now run
 * here: the stream, banner/panel status and persistence survive navigation, and the mounted
 * editor rehydrates from the store and re-fetches once a background persist completes.
 */
export const useIqCheckpointSuggestStore = defineStore("iqCheckpointSuggest", () => {
  const notificationStore = useNotificationStore();
  const iqChatStore = useIQChatStore();

  // checkpointId -> run (reactive)
  const quizRuns = reactive({});
  const inspectionRuns = reactive({});
  // ER-12788 — checkpointId -> questions the last Suggest Quiz replaced. Session memory only:
  // never persisted, and dropped when the banner is closed or the editor is left.
  const quizUndoSnapshots = reactive({});
  const version = ref(0);
  function bumpVersion() {
    version.value += 1;
  }

  // kind:checkpointId -> non-reactive coordination { token, controller, stallTimer }
  const coords = {};

  function keyOf(id) {
    return id == null ? null : String(id);
  }

  function coord(kind, id) {
    const key = `${kind}:${keyOf(id)}`;
    if (!coords[key]) coords[key] = { token: 0, controller: null, stallTimer: null };
    return coords[key];
  }

  function getQuizRun(id) {
    void version.value;
    const key = keyOf(id);
    return key == null ? null : quizRuns[key] || null;
  }
  function getInspectionRun(id) {
    void version.value;
    const key = keyOf(id);
    return key == null ? null : inspectionRuns[key] || null;
  }
  function getQuizUndoSnapshot(id) {
    void version.value;
    const key = keyOf(id);
    return key == null ? null : quizUndoSnapshots[key] || null;
  }

  /** Forfeit undo: drop the stored snapshot and the one an in-flight Suggest would store. */
  function clearQuizUndoSnapshot(id) {
    const key = keyOf(id);
    if (key == null) return;
    if (quizRuns[key]) quizRuns[key].undoSnapshot = null;
    if (quizUndoSnapshots[key]) {
      del(quizUndoSnapshots, key);
      bumpVersion();
    }
  }

  function beginThinking() {
    try {
      iqChatStore.startThinking();
      iqChatStore.updateCurrentReasoning("");
    } catch {
      // Pinia may be unavailable in isolated unit tests.
    }
  }
  function endThinking() {
    try {
      iqChatStore.stopThinking();
      iqChatStore.updateCurrentReasoning("");
    } catch {
      // Pinia may be unavailable in isolated unit tests.
    }
  }

  function clearStallTimer(kind, id) {
    const c = coords[`${kind}:${keyOf(id)}`];
    if (!c || !c.stallTimer) return;
    clearTimeout(c.stallTimer);
    c.stallTimer = null;
  }

  function scheduleStall(kind, id, isVideo) {
    clearStallTimer(kind, id);
    if (!isVideo) return;
    const c = coord(kind, id);
    c.stallTimer = setTimeout(() => {
      const run = kind === "quiz" ? getQuizRun(id) : getInspectionRun(id);
      if (!run || run.status !== "thinking") return;
      run.videoStatus = "stalled";
      endThinking();
      try {
        c.controller?.abort();
      } catch {
        // AbortController may be unavailable in isolated unit tests.
      }
    }, QUIZ_SUGGEST_STALL_TIMEOUT_MS);
  }

  function abortRun(kind, id) {
    const key = keyOf(id);
    if (key == null) return;
    const c = coord(kind, id);
    c.token += 1;
    clearStallTimer(kind, id);
    if (c.controller) {
      try {
        c.controller.abort();
      } catch {
        // AbortController may be unavailable in isolated unit tests.
      }
      c.controller = null;
    }
  }

  // ── Suggest quiz ────────────────────────────────────────────────────────────
  async function clearProposedQuizForNewSuggestion(accountId, ctx, token) {
    // Server-side clear only — UI refreshes once after new questions are saved.
    if (!ctx.hasExistingQuiz) return;
    await changeCheckpointV2(accountId, ctx.responsibilityId, ctx.checkpointId, {
      proposed_quiz: null,
      proposed_quiz_is_autopass: false,
    });
    if (token !== coord("quiz", ctx.checkpointId).token) return;
  }

  async function runQuiz(ctx) {
    const c = coord("quiz", ctx.checkpointId);
    const token = c.token;
    if (c.controller) c.controller.abort();
    c.controller = new AbortController();
    scheduleStall("quiz", ctx.checkpointId, ctx.isVideo);

    const run = getQuizRun(ctx.checkpointId);
    try {
      let completeData = null;
      await suggestQuizStream({
        checkpointCriteria: ctx.checkpointCriteria,
        microlearningContent: ctx.microlearningContent,
        existingQuestions: ctx.existingQuestions || [],
        remainingSlots: ctx.remainingSlots ?? MAX_CHECKPOINT_QUIZ_QUESTIONS,
        signal: c.controller.signal,
        onThinkingUpdate: (text) => {
          scheduleStall("quiz", ctx.checkpointId, ctx.isVideo);
          if (ctx.isVideo && getQuizRun(ctx.checkpointId)?.videoStatus === "connecting") {
            getQuizRun(ctx.checkpointId).videoStatus = "streaming";
          }
          // Article/root suggestions render the streamed reasoning in IQThinkingBanner.
          if (!ctx.isVideo) {
            try {
              iqChatStore.updateCurrentReasoning(text);
            } catch {
              // Pinia may be unavailable in isolated unit tests.
            }
          }
        },
        onComplete: (data) => {
          completeData = data;
        },
      });

      if (token !== coord("quiz", ctx.checkpointId).token) return;
      clearStallTimer("quiz", ctx.checkpointId);
      // Stream done — freeze the banner on its last thinking line while the quiz persists.
      try {
        iqChatStore.stopThinking();
      } catch {
        // Pinia may be unavailable in isolated unit tests.
      }

      const suggestedRows = mapSuggestedQuizQuestionsToApi(completeData?.questions);
      const currentRun = getQuizRun(ctx.checkpointId);
      if (!currentRun) return;
      if (!suggestedRows.length) {
        failQuiz(ctx);
        notificationStore.error("No quiz questions were suggested. Try again.");
        return;
      }

      const normalizedSuggested = mapSuggestedQuizQuestionsToApi(completeData?.questions, 1).slice(
        0,
        MAX_CHECKPOINT_QUIZ_QUESTIONS
      );

      const accountId = ctx.accountId ?? getAccountId();
      if (!accountId || ctx.responsibilityId == null || ctx.checkpointId == null) {
        failQuiz(ctx);
        return;
      }

      try {
        await clearProposedQuizForNewSuggestion(accountId, ctx, token);
        if (token !== coord("quiz", ctx.checkpointId).token) return;
        for (const q of normalizedSuggested) {
          await addCheckpointQuestion(accountId, ctx.responsibilityId, ctx.checkpointId, {
            question: { q_text: q.q_text, q_answers: q.q_answers },
          });
        }
        if (token !== coord("quiz", ctx.checkpointId).token) return;
        clearStallTimer("quiz", ctx.checkpointId);
        succeedQuiz(ctx);
        notificationStore.success(
          normalizedSuggested.length === 1
            ? "1 quiz question suggested."
            : `${normalizedSuggested.length} quiz questions suggested.`
        );
      } catch (err) {
        if (token !== coord("quiz", ctx.checkpointId).token) return;
        console.error("[iqCheckpointSuggestStore] persist suggested quiz questions:", err);
        failQuiz(ctx);
        notificationStore.error(
          err?.response?.data?.detail ??
            err?.response?.data?.message ??
            "Could not save suggested quiz questions."
        );
      }
      return;
    } catch (err) {
      if (err?.name === "AbortError" || err?.name === "CanceledError") return;
      if (token !== coord("quiz", ctx.checkpointId).token) return;
      console.error("[iqCheckpointSuggestStore] suggest quiz failed:", err);
      failQuiz(ctx);
      notificationStore.error(err?.message || "Could not suggest quiz questions.");
    } finally {
      if (token === coord("quiz", ctx.checkpointId).token) {
        clearStallTimer("quiz", ctx.checkpointId);
        coord("quiz", ctx.checkpointId).controller = null;
      }
    }
  }

  function succeedQuiz(ctx) {
    const run = getQuizRun(ctx.checkpointId);
    if (!run) return;
    run.status = "done";
    run.bannerState = null;
    run.videoStatus = null;
    run.error = null;
    run.finishedAt = Date.now();
    run.refreshPending = true;
    if (run.undoSnapshot?.length) {
      set(quizUndoSnapshots, keyOf(ctx.checkpointId), run.undoSnapshot);
    }
    run.undoSnapshot = null;
    bumpVersion();
  }

  function failQuiz(ctx) {
    const run = getQuizRun(ctx.checkpointId);
    if (!run) return;
    run.status = "failed";
    run.error = "Could not suggest quiz questions.";
    if (run.isVideo) {
      run.videoStatus = "stalled";
      endThinking();
    } else {
      run.bannerState = "failed";
      endThinking();
    }
    run.finishedAt = Date.now();
    bumpVersion();
  }

  function startQuiz(ctx) {
    const key = keyOf(ctx.checkpointId);
    if (key == null) return null;
    abortRun("quiz", ctx.checkpointId);
    clearQuizUndoSnapshot(ctx.checkpointId);
    const run = makeRun("quiz", ctx.isVideo);
    if (Array.isArray(ctx.previousQuestions) && ctx.previousQuestions.length) {
      run.undoSnapshot = ctx.previousQuestions;
    }
    set(quizRuns, key, run);
    bumpVersion();
    beginThinking();
    void runQuiz(ctx);
    return quizRuns[key];
  }

  function retryQuiz(ctx) {
    return startQuiz(ctx);
  }

  /** ER-12788 — restore the replaced questions through the atomic replace endpoint. */
  async function undoQuizSuggestion({ accountId, responsibilityId, checkpointId }) {
    const snapshot = getQuizUndoSnapshot(checkpointId);
    if (!snapshot) return false;
    await replaceCheckpointQuestions(accountId, responsibilityId, checkpointId, {
      questions: snapshot,
    });
    clearQuizUndoSnapshot(checkpointId);
    return true;
  }

  // ── Suggest inspection protocol ─────────────────────────────────────────────
  async function clearProposedInspectionStepsForNewSuggestion(accountId, ctx, token) {
    const existing = Array.isArray(ctx.existingInspectionSteps)
      ? [...ctx.existingInspectionSteps]
      : [];
    if (!existing.length) return;
    // Delete steps individually so requires_confirmation_at_every_step is preserved.
    for (let i = existing.length - 1; i >= 0; i -= 1) {
      if (token !== coord("inspection", ctx.checkpointId).token) return;
      const stepId = existing[i]?.step_sequence ?? i + 1;
      await deleteCheckpointInspectionStep(
        accountId,
        ctx.responsibilityId,
        ctx.checkpointId,
        stepId
      );
    }
    if (token !== coord("inspection", ctx.checkpointId).token) return;
  }

  async function runInspection(ctx) {
    const c = coord("inspection", ctx.checkpointId);
    const token = c.token;
    if (c.controller) c.controller.abort();
    c.controller = new AbortController();
    scheduleStall("inspection", ctx.checkpointId, ctx.isVideo);

    let streamFailed = false;
    try {
      let completeData = null;
      await suggestInspectionProtocolStream({
        checkpointCriteria: ctx.checkpointCriteria,
        microlearningContent: ctx.microlearningContent,
        existingSteps: ctx.existingSteps || [],
        signal: c.controller.signal,
        timeoutMs: INSPECTION_STREAM_TIMEOUT_MS,
        onThinkingUpdate: (text) => {
          scheduleStall("inspection", ctx.checkpointId, ctx.isVideo);
          if (ctx.isVideo && getInspectionRun(ctx.checkpointId)?.videoStatus === "connecting") {
            getInspectionRun(ctx.checkpointId).videoStatus = "streaming";
          }
          // Article/root suggestions render the streamed reasoning in IQThinkingBanner.
          if (!ctx.isVideo) {
            try {
              iqChatStore.updateCurrentReasoning(text);
            } catch {
              // Pinia may be unavailable in isolated unit tests.
            }
          }
        },
        onComplete: (data) => {
          completeData = data;
        },
        onError: () => {
          streamFailed = true;
          failInspection(ctx);
        },
      });

      if (token !== coord("inspection", ctx.checkpointId).token) return;
      if (streamFailed) return;
      clearStallTimer("inspection", ctx.checkpointId);
      try {
        iqChatStore.stopThinking();
      } catch {
        // Pinia may be unavailable in isolated unit tests.
      }

      const suggestedRows = filterDuplicateSuggestedInspectionSteps(
        mapSuggestedStepsToEditorRows(completeData?.steps),
        ctx.existingSteps || []
      ).slice(0, MAX_INSPECTION_STEPS);

      const run = getInspectionRun(ctx.checkpointId);
      if (!run) return;
      if (!suggestedRows.length) {
        run.bannerState = null;
        run.videoStatus = null;
        run.status = "done";
        run.refreshPending = true;
        bumpVersion();
        endThinking();
        notificationStore.info(
          "No steps suggested",
          "IQ could not suggest inspection steps for this checkpoint."
        );
        return;
      }

      const appended = suggestedRows.map((row, index) =>
        mapInstructionStep({ ...row, step_sequence: index + 1 })
      );

      const accountId = ctx.accountId ?? getAccountId();
      if (!accountId || ctx.responsibilityId == null || ctx.checkpointId == null) {
        failInspection(ctx);
        return;
      }

      try {
        await clearProposedInspectionStepsForNewSuggestion(accountId, ctx, token);
        if (token !== coord("inspection", ctx.checkpointId).token) return;
        for (const row of appended) {
          const body = buildAddInspectionStepApiPayload(row);
          await addCheckpointInspectionStep(
            accountId,
            ctx.responsibilityId,
            ctx.checkpointId,
            body
          );
        }
        if (token !== coord("inspection", ctx.checkpointId).token) return;
        clearStallTimer("inspection", ctx.checkpointId);
        succeedInspection(ctx, { switchToProposedTab: !!ctx.isActiveCheckpoint });
        notificationStore.success(
          appended.length === 1
            ? "1 inspection step suggested."
            : `${appended.length} inspection steps suggested.`
        );
      } catch (err) {
        if (token !== coord("inspection", ctx.checkpointId).token) return;
        console.error("[iqCheckpointSuggestStore] persist suggested inspection steps:", err);
        failInspection(ctx);
        notificationStore.error(
          err?.response?.data?.detail ??
            err?.response?.data?.message ??
            "Could not save suggested inspection steps."
        );
      }
      return;
    } catch (err) {
      if (err?.name === "AbortError" || err?.name === "CanceledError") return;
      if (token !== coord("inspection", ctx.checkpointId).token) return;
      console.error("[iqCheckpointSuggestStore] suggest inspection protocol failed:", err);
      failInspection(ctx);
      notificationStore.error(err?.message || "Could not suggest inspection steps.");
    } finally {
      if (token === coord("inspection", ctx.checkpointId).token) {
        clearStallTimer("inspection", ctx.checkpointId);
        coord("inspection", ctx.checkpointId).controller = null;
      }
    }
  }

  function succeedInspection(ctx, { switchToProposedTab = false } = {}) {
    const run = getInspectionRun(ctx.checkpointId);
    if (!run) return;
    run.status = "done";
    run.bannerState = null;
    run.videoStatus = null;
    run.error = null;
    run.finishedAt = Date.now();
    run.refreshPending = true;
    run.switchToProposedTab = switchToProposedTab;
    bumpVersion();
  }

  function failInspection(ctx) {
    const run = getInspectionRun(ctx.checkpointId);
    if (!run) return;
    run.status = "failed";
    run.error = "Could not suggest inspection steps.";
    if (run.isVideo) {
      run.videoStatus = "stalled";
      endThinking();
    } else {
      run.bannerState = "failed";
      endThinking();
    }
    run.finishedAt = Date.now();
    bumpVersion();
  }

  function startInspection(ctx) {
    const key = keyOf(ctx.checkpointId);
    if (key == null) return null;
    abortRun("inspection", ctx.checkpointId);
    set(inspectionRuns, key, makeRun("inspection", ctx.isVideo));
    bumpVersion();
    beginThinking();
    void runInspection(ctx);
    return inspectionRuns[key];
  }

  function retryInspection(ctx) {
    return startInspection(ctx);
  }

  /** Editor acked a completed background persist — clear the refresh flags. */
  function acknowledgeRefresh(kind, id) {
    const run = kind === "quiz" ? getQuizRun(id) : getInspectionRun(id);
    if (!run) return;
    run.refreshPending = false;
    run.switchToProposedTab = false;
    bumpVersion();
  }

  function abortAllForCheckpoint(id) {
    abortRun("quiz", id);
    abortRun("inspection", id);
    const key = keyOf(id);
    if (key == null) return;
    clearQuizUndoSnapshot(id);
    if (quizRuns[key]) del(quizRuns, key);
    if (inspectionRuns[key]) del(inspectionRuns, key);
    bumpVersion();
    endThinking();
  }

  /** Session teardown (logout / account switch). */
  function clearAll() {
    Object.keys(quizRuns).forEach((key) => {
      abortRun("quiz", key);
      del(quizRuns, key);
    });
    Object.keys(inspectionRuns).forEach((key) => {
      abortRun("inspection", key);
      del(inspectionRuns, key);
    });
    Object.keys(quizUndoSnapshots).forEach((key) => del(quizUndoSnapshots, key));
    Object.keys(coords).forEach((key) => {
      clearStallTimerByKey(key);
      delete coords[key];
    });
    bumpVersion();
    endThinking();
  }

  function clearStallTimerByKey(key) {
    const c = coords[key];
    if (c && c.stallTimer) {
      clearTimeout(c.stallTimer);
      c.stallTimer = null;
    }
  }

  return {
    quizRuns,
    inspectionRuns,
    quizUndoSnapshots,
    version,
    getQuizRun,
    getInspectionRun,
    getQuizUndoSnapshot,
    clearQuizUndoSnapshot,
    startQuiz,
    retryQuiz,
    undoQuizSuggestion,
    startInspection,
    retryInspection,
    acknowledgeRefresh,
    abortAllForCheckpoint,
    clearAll,
  };
});
